import { createContext, ReactNode, useState } from "react";
import { LocalUserChoices } from "@livekit/components-react";
import { VideoRoom } from "protogen/video_pb";

export interface VideoCallState {
  activeVideoRoom: VideoRoom | null;
  token: string | null;
  userChoices: LocalUserChoices | null;
  isPipMode: boolean;
  isMinimized: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  shouldRenderLiveKit: boolean;
  renderLocation: "main" | "pip" | "none";
  manuallyEnded: boolean;
  connectionTime: Date | null;
}

type VideoCallContextType = {
  videoCallState: VideoCallState;
  startVideoCall: (
    room: VideoRoom,
    token: string,
    userChoices?: LocalUserChoices,
  ) => void;
  endVideoCall: () => void;
  manualEndVideoCall: () => void;
  resetManuallyEnded: () => void;
  togglePipMode: () => void;
  toggleMinimized: () => void;
  updateUserChoices: (choices: LocalUserChoices) => void;
  updateConnectionState: (connected: boolean, connecting: boolean) => void;
  setRenderLocation: (location: "main" | "pip" | "none") => void;
  toggleRenderLocation: () => void;
};

const defaultVideoCallState: VideoCallState = {
  activeVideoRoom: null,
  token: null,
  userChoices: null,
  isPipMode: false,
  isMinimized: false,
  isConnected: false,
  isConnecting: false,
  shouldRenderLiveKit: false,
  renderLocation: "none",
  manuallyEnded: false,
  connectionTime: null,
};

export const VideoCallContext = createContext<VideoCallContextType>({
  videoCallState: defaultVideoCallState,
  startVideoCall: () => {},
  endVideoCall: () => {},
  manualEndVideoCall: () => {},
  resetManuallyEnded: () => {},
  togglePipMode: () => {},
  toggleMinimized: () => {},
  updateUserChoices: () => {},
  updateConnectionState: () => {},
  setRenderLocation: () => {},
  toggleRenderLocation: () => {},
});

export const VideoCallContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [videoCallState, setVideoCallState] = useState<VideoCallState>(
    defaultVideoCallState,
  );
  const startVideoCall = (
    room: VideoRoom,
    token: string,
    userChoices?: LocalUserChoices,
  ) => {
    setVideoCallState({
      activeVideoRoom: room,
      token,
      userChoices: userChoices || null,
      isPipMode: false,
      isMinimized: false,
      isConnecting: true,
      isConnected: false,
      shouldRenderLiveKit: true,
      renderLocation: "main",
      manuallyEnded: false,
      connectionTime: null,
    });
  };

  const endVideoCall = () => {
    setVideoCallState(defaultVideoCallState);
  };

  const manualEndVideoCall = () => {
    setVideoCallState({
      ...defaultVideoCallState,
      manuallyEnded: true,
    });
  };

  const resetManuallyEnded = () => {
    setVideoCallState({
      ...videoCallState,
      manuallyEnded: false,
      isPipMode: false,
      renderLocation: "main",
      shouldRenderLiveKit: videoCallState.activeVideoRoom !== null,
      connectionTime: null,
    });
  };

  const togglePipMode = () => {
    setVideoCallState({
      ...videoCallState,
      isPipMode: !videoCallState.isPipMode,
    });
  };

  const toggleMinimized = () => {
    setVideoCallState({
      ...videoCallState,
      isMinimized: !videoCallState.isMinimized,
    });
  };

  const updateUserChoices = (choices: LocalUserChoices) => {
    setVideoCallState({
      ...videoCallState,
      userChoices: choices,
    });
  };

  const updateConnectionState = (connected: boolean, connecting: boolean) => {
    setVideoCallState((prevState) => ({
      ...prevState,
      isConnected: connected,
      isConnecting: connecting,
      connectionTime:
        connected && !prevState.isConnected
          ? new Date()
          : prevState.connectionTime,
    }));
  };

  const setRenderLocation = (location: "main" | "pip" | "none") => {
    setVideoCallState({
      ...videoCallState,
      renderLocation: location,
      shouldRenderLiveKit: location !== "none",
      isPipMode: location === "pip",
    });
  };

  const toggleRenderLocation = () => {
    const currentLocation = videoCallState.renderLocation;
    const newLocation = currentLocation === "main" ? "pip" : "main";
    setRenderLocation(newLocation);
  };

  return (
    <VideoCallContext.Provider
      value={{
        videoCallState,
        startVideoCall,
        endVideoCall,
        manualEndVideoCall,
        resetManuallyEnded,
        togglePipMode,
        toggleMinimized,
        updateUserChoices,
        updateConnectionState,
        setRenderLocation,
        toggleRenderLocation,
      }}
    >
      {children}
    </VideoCallContext.Provider>
  );
};
